import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {forkJoin, Observable, of, ReplaySubject} from "rxjs";

import {
  Coordinator,
  DobryAgentInsuranceCompany,
  FacilityDobryAgent, ImageResponsive, OpeningHoursDaysOrder,
  OpeningHoursViewItem,
  PaymentMethod,
  SubCategory
} from "../../../shared/interface/facility-dobry-agent";
import {AuthService} from "../../../shared/services/auth.service";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {catchError, map, shareReplay, switchMap, takeUntil} from "rxjs/operators";
import {OfwcaWithUser360Profiles, User} from "../../../shared/interface/user";
import {PartnerFacility} from "../../../shared/interface/partner-facility";
import {environment} from "../../../../environments/environment";
import {Ofwca} from "../../../shared/interface/ofwca";
import {MatTableDataSource} from "@angular/material/table";
import {UserService} from "../../../shared/services/user.service";


@Component({
  selector: 'app-facility-dobry-agent-view',
  templateUrl: './facility-dobry-agent-view.component.html',
  styleUrls: ['./facility-dobry-agent-view.component.scss']
})
export class FacilityDobryAgentViewComponent implements OnInit, OnDestroy {
  @Input() facility: Observable<PartnerFacility>;
  @Input() facilityDobryAgent: Observable<FacilityDobryAgent>;
  @Input() ofwcas: Observable<Ofwca[]>;
  @Output() modifyClicked = new EventEmitter<any>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  partnerFacility?: PartnerFacility = null;
  facilityDobryAgentObject?: FacilityDobryAgent = null;
  userPermissions: ReplaySubject<UserPermissions>;
  ofwcasDataSource: MatTableDataSource<OfwcaWithUser360Profiles> = new MatTableDataSource<OfwcaWithUser360Profiles>([]);
  displayedColumns: string[] = ['fullName', 'status', 'phone', 'email', 'portalDa', 'blog', 'edit', 'avatar'];

  dobryAgentBaseUrl = environment.dobryAgentUrl;

  readonly openingHoursDaysOrder = OpeningHoursDaysOrder;
  constructor(
    private auth: AuthService,
    private userService: UserService,
  ) {
    this.userPermissions = auth.getUserPermissions();

  }

  ngOnInit(): void {
    this.facility
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facility) => {
        this.partnerFacility = facility;
      });
    this.facilityDobryAgent
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facilityDobryAgent: FacilityDobryAgent) => {
        this.facilityDobryAgentObject = facilityDobryAgent;
      })
    this.ofwcas
      .pipe(
        switchMap((ofwcas: Ofwca[]) => {
          const requests = ofwcas.map((ofwca: Ofwca) => {
            const users360 = ofwca.user360 ?? [];

            if (!users360.length) {
              return of({
                ...ofwca,
                user360Profiles: [],
              } as OfwcaWithUser360Profiles);
            }

            const userRequests = users360
              .filter((user) => !!user.uuid)
              .map((user) => this.getUser360Profile(user.uuid));

            if (!userRequests.length) {
              return of({
                ...ofwca,
                user360Profiles: [],
              } as OfwcaWithUser360Profiles);
            }

            return forkJoin(userRequests).pipe(
              map((profiles) => ({
                ...ofwca,
                user360Profiles: profiles.filter((profile): profile is User => !!profile),
              } as OfwcaWithUser360Profiles))
            );
          });

          return requests.length ? forkJoin(requests) : of([]);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((ofwcas: OfwcaWithUser360Profiles[]) => {
        this.ofwcasDataSource.data = ofwcas;
      });
  }
  private readonly user360ProfileCache = new Map<string, Observable<User | null>>();
  private getUser360Profile(uuid: string): Observable<User | null> {
    if (!this.user360ProfileCache.has(uuid)) {
      this.user360ProfileCache.set(
        uuid,
        this.userService.get(uuid).pipe(
          catchError(() => of(null)),
          shareReplay(1)
        )
      );
    }

    return this.user360ProfileCache.get(uuid);
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }


  getOpeningHoursItems(): OpeningHoursViewItem[] {
    const openHours = this.facilityDobryAgentObject?.openHours;

    if (!openHours) {
      return [];
    }

    return this.openingHoursDaysOrder.map((day) => {
      const item = openHours[day.key];

      const openTime = item?.openTime ?? '';
      const closeTime = item?.closeTime ?? '';

      const closed =
        openTime.toLowerCase() === 'nieczynne'
        || closeTime.toLowerCase() === 'nieczynne'
        || (!openTime && !closeTime);

      return {
        key: day.key,
        label: day.label,
        openTime,
        closeTime,
        closed,
      };
    });
  }
  getActivePaymentMethods(): PaymentMethod[] {
    return this.facilityDobryAgentObject?.paymentMethods
      ?.filter((method: PaymentMethod) => method.active) ?? [];
  }

  getPaymentMethodIcon(method: PaymentMethod): string {
    if (method.icon) {
      return method.icon;
    }

    switch (method.slug) {
      case 'blik':
        return 'smartphone';
      case 'card':
        return 'credit_card';
      case 'cash':
        return 'payments';
      case 'transfer':
        return 'account_balance';
      default:
        return 'payment';
    }
  }

  getPaymentMethodsLabel(): string {
    const methods = this.getActivePaymentMethods();

    if (methods.length === 0) {
      return 'Brak danych';
    }

    return methods.map((method: PaymentMethod) => method.name).join(', ');
  }

  getActiveSubCategories(): SubCategory[] {
    return this.facilityDobryAgentObject?.subCategories
      ?.filter((subCategory: SubCategory) => {
        return subCategory.active !== false;
      })
      ?.sort((a: SubCategory, b: SubCategory) => {
        const positionA = a.position ?? 0;
        const positionB = b.position ?? 0;

        if (positionA !== positionB) {
          return positionA - positionB;
        }

        return (a.name ?? '').localeCompare(b.name ?? '');
      }) ?? [];
  }

  getInsuranceCompanies(): DobryAgentInsuranceCompany[] {
    return this.facilityDobryAgentObject?.insuranceCompanies
      ?.sort((a: DobryAgentInsuranceCompany, b: DobryAgentInsuranceCompany) => {
        return (a.name ?? '').localeCompare(b.name ?? '');
      }) ?? [];
  }

  getChronieUrl(url?: string | null): string | null {
    if (!url) {
      return null;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `https://${url}`;
  }


  getImageUrl(url?: string | null): string | null {
    if (!url) {
      return null;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `${environment.dobryAgentUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  }

  getImageSrcset(srcset?: string | null): string | null {
    if (!srcset) {
      return null;
    }

    return srcset
      .split(',')
      .map((item: string) => {
        const trimmed = item.trim();

        if (!trimmed) {
          return '';
        }

        const parts = trimmed.split(/\s+/);
        const url = parts[0];
        const descriptor = parts.slice(1).join(' ');

        const fullUrl = this.getImageUrl(url);

        return descriptor ? `${fullUrl} ${descriptor}` : fullUrl;
      })
      .filter(Boolean)
      .join(', ');
  }

  private readonly applicationRoleOrder = [
    'none',
    'active',
  ];

  private getPrimaryUser360(element: OfwcaWithUser360Profiles): User | null {
    if (!element?.user360Profiles || element.user360Profiles.length === 0) {
      return null;
    }

    return element.user360Profiles[0];
  }

  private getRolePriority(role: string): number {
    const index = this.applicationRoleOrder.indexOf(role);

    return index === -1 ? 0 : index;
  }

  private hasRoleAtLeast(role: string, minimumRole: string): boolean {
    return this.getRolePriority(role) >= this.getRolePriority(minimumRole);
  }

  private normalizeApplicationPermissions(applicationPermissions: any): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions.forEach((item: any) => {
      if (item.applicationKey) {
        result[item.applicationKey] = item.permissions || {};
      }
    });

    return result;
  }

  private normalizeDepartmentApplicationPermissions(
    applicationPermissions: any,
    assignmentType: 'employee' | 'supervisor'
  ): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions
      .filter((item: any) => item.assignmentType === assignmentType)
      .forEach((item: any) => {
        result[item.applicationKey] = item.permissions || {};
      });

    return result;
  }

  private getEffectiveDobryAgentPermissionsFromUser(user: User): any {
    if (!user) {
      return {};
    }

    const userPermissions = this.normalizeApplicationPermissions(user.applicationPermissions);
    const userDobryAgentPermissions = userPermissions?.dobryagent || {};

    let departmentDobryAgentPermissions = {};

    if (user.department?.applicationPermissions) {
      const departmentPermissions = this.normalizeDepartmentApplicationPermissions(
        user.department.applicationPermissions,
        'employee'
      );

      departmentDobryAgentPermissions = departmentPermissions?.dobryagent || {};
    }

    return {
      /**
       * Dostęp do aplikacji może pochodzić z działu albo z użytkownika.
       * Najpierw bierzemy dział, a jeśli go nie ma, indywidualne.
       */
      application_access:
        departmentDobryAgentPermissions?.['application_access']
        ?? userDobryAgentPermissions?.['application_access']
        ?? 'none',

      /**
       * Blog zawsze indywidualnie.
       */
      blog: userDobryAgentPermissions?.['blog'] ?? 'none',

      /**
       * Wizytówka zawsze indywidualnie.
       */
      business_card: userDobryAgentPermissions?.['business_card'] ?? 'none',
    };
  }

  getDobryAgentPermission(element: any, module: string): string {
    const user = this.getPrimaryUser360(element);

    if (!user) {
      return 'none';
    }

    const permissions = this.getEffectiveDobryAgentPermissionsFromUser(user);

    return permissions?.[module] || 'none';
  }

  hasDobryAgentApplicationAccess(element: any): boolean {
    return this.getDobryAgentPermission(element, 'application_access') === 'active';
  }

  hasDobryAgentBlogAccess(element: any): boolean {
    if (!this.hasDobryAgentApplicationAccess(element)) {
      return false;
    }

    const role = this.getDobryAgentPermission(element, 'blog');

    return this.hasRoleAtLeast(role, 'active');
  }

  hasDobryAgentBusinessCardEditAccess(element: any): boolean {
    if (!this.hasDobryAgentApplicationAccess(element)) {
      return false;
    }

    const role = this.getDobryAgentPermission(element, 'business_card');

    return this.hasRoleAtLeast(role, 'active');
  }

  getDobryAgentPermissionSourceLabel(element: any, module?: string): string {
    const user = this.getPrimaryUser360(element);

    if (!user) {
      return 'Brak konta ASF360';
    }

    if (module === 'blog' || module === 'business_card') {
      return 'Indywidualne';
    }

    if (module === 'application_access') {
      if (user.department?.applicationPermissions) {
        const departmentPermissions = this.normalizeDepartmentApplicationPermissions(
          user.department.applicationPermissions,
          'employee'
        );

        if (departmentPermissions?.dobryagent?.application_access) {
          return `Dział: ${user.department.name}`;
        }
      }

      return 'Indywidualne';
    }

    return 'Indywidualne';
  }

  getDobryAgentCoordinator(ofwca: Ofwca): any | null {
    const coordinators = this.facilityDobryAgentObject?.coordinators ?? [];

    return coordinators.find((coordinator: any) => {
      return coordinator.ofwca24Id === ofwca.uuid;
    }) ?? null;
  }

  isOfwcaCoordinator(ofwca: Ofwca): boolean {
    return !!this.getDobryAgentCoordinator(ofwca);
  }

  isOfwcaActiveCoordinator(ofwca: Ofwca): boolean {
    const coordinator = this.getDobryAgentCoordinator(ofwca);

    return !!coordinator && coordinator.active === true;
  }

  getDobryAgentOrOfwcaPhone(element: Ofwca): string {
    const coordinator = this.getDobryAgentCoordinator(element);

    if (coordinator?.active === true && coordinator?.phone) {
      return coordinator.phone;
    }

    return element?.phone ?? '';
  }

  getDobryAgentOrOfwcaEmail(element: Ofwca): string {
    const coordinator = this.getDobryAgentCoordinator(element);

    if (coordinator?.active === true && coordinator?.email) {
      return coordinator.email;
    }

    return element?.email ?? '';
  }


  getAgentAvatarUrl(element: OfwcaWithUser360Profiles): string {
    const previewUrl = (element as any)?.avatarPreviewUrl;

    if (previewUrl) {
      return previewUrl;
    }

    const coordinator = this.getDobryAgentCoordinator(element);

    const imageUrl =
      this.getImageVariantUrl(coordinator?.featuredImage, 'medium')
      ?? coordinator?.featuredImage?.src
      ?? this.getImageVariantUrl((element as any)?.featuredImage, 'medium')
      ?? (element as any)?.featuredImage?.src
      ?? '';

    if (!imageUrl) {
      return '';
    }

    if (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://') ||
      imageUrl.startsWith('data:image')
    ) {
      return imageUrl;
    }

    return `${this.dobryAgentBaseUrl.replace(/\/$/, '')}/${imageUrl.replace(/^\//, '')}`;
  }


  private getImageVariantUrl(
    image: ImageResponsive | null | undefined,
    preferredSize: 'small' | 'medium' | 'large' | 'original' = 'medium'
  ): string | null {
    if (!image) {
      return null;
    }

    const preferredVariant = image.variants?.find(
      (variant) => variant.size === preferredSize
    );

    if (preferredVariant?.url) {
      return preferredVariant.url;
    }

    return image.src ?? null;
  }
}
